/**
 * Locale-store: houdt de actieve taal bij, bewaart de voorkeur en houdt
 * <html lang> synchroon (belangrijk voor toegankelijkheid en SEO). De
 * vue-i18n-instantie blijft de bron voor vertalingen; deze store is de
 * schil eromheen voor persistentie en het schakelen van taal.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import i18n, {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  resolveInitialLocale,
  type AppLocale,
} from '../i18n'

function applyHtmlLang(locale: AppLocale) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', locale)
  }
}

export const useLocaleStore = defineStore('locale', () => {
  const locale = ref<AppLocale>(resolveInitialLocale())
  applyHtmlLang(locale.value)

  /**
   * Zet de actieve taal. `persist=false` voor tijdelijke, route-gestuurde
   * taal (marketing /nl vs /landing) die de opgeslagen voorkeur van de
   * gebruiker niet mag overschrijven.
   */
  function setLocale(next: AppLocale, persist = true) {
    if (!isSupportedLocale(next)) next = DEFAULT_LOCALE
    locale.value = next
    i18n.global.locale.value = next
    applyHtmlLang(next)
    if (persist && typeof window !== 'undefined') {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next)
    }
  }

  function toggle() {
    setLocale(locale.value === 'en' ? 'nl' : 'en')
  }

  /**
   * Terug naar de opgeslagen voorkeur (of standaard). Gebruikt door de
   * router-guard bij routes zónder afgedwongen taal, zodat een tijdelijke
   * marketing-locale (/nl) niet doorlekt naar de rest van de app.
   */
  function restorePreferred() {
    setLocale(resolveInitialLocale(), false)
  }

  return { locale, setLocale, toggle, restorePreferred }
})
